// src/components/RemoteConnectionManager.jsx
import React, { useState, useEffect } from 'react';
import { Monitor, CheckCircle, XCircle, Edit2, Save } from 'lucide-react';
import { ExternalLink, Copy, X, Eye, EyeOff } from './icons';
import { useToast } from '../contexts/ToastContext';
import { activosAPI } from '../services/api';

export default function RemoteConnectionManager({ activoData, onDataUpdated }) {
    const { showToast } = useToast();
    const [copiedProtocol, setCopiedProtocol] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [connectionData, setConnectionData] = useState({
        rustDeskId: '',
        rustDeskPassword: '',
        anyDeskId: '',
        anyDeskPassword: '',
        ipAddress: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        rustDesk: false,
        anyDesk: false
    });

    // Configuración de conexiones remotas - Solo AnyDesk y RustDesk
    const connectionConfigs = [
        {
            name: 'AnyDesk',
            protocol: 'anydesk',
            icon: '🖥️',
            description: 'Conexión remota AnyDesk',
            getUrl: (id) => `anydesk://${id}`,
            getDisplayUrl: (id) => `anydesk://${id}`,
            requiresClient: true,
            clientName: 'AnyDesk',
            idField: 'anyDeskId',
            passwordField: 'anyDeskPassword'
        },
        {
            name: 'RustDesk',
            protocol: 'rustdesk',
            icon: '🔧',
            description: 'Conexión remota RustDesk',
            getUrl: (id) => `rustdesk://${id}`,
            getDisplayUrl: (id) => `rustdesk://${id}`,
            requiresClient: true,
            clientName: 'RustDesk',
            idField: 'rustDeskId',
            passwordField: 'rustDeskPassword'
        }
    ];

    // Cargar datos de conexión cuando se monta el componente
    useEffect(() => {
        if (activoData) {

            const newConnectionData = {
                rustDeskId: activoData.RustDeskId || activoData.rustDeskId || '',
                rustDeskPassword: activoData.RustDeskPassword || activoData.rustDeskPassword || '',
                anyDeskId: activoData.AnyDeskId || activoData.anyDeskId || '',
                anyDeskPassword: activoData.AnyDeskPassword || activoData.anyDeskPassword || '',
                ipAddress: activoData.IpAddress || activoData.ipAddress || ''
            };

            setConnectionData(newConnectionData);
        }
    }, [activoData]);

    const copyToClipboard = async (text, protocol) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedProtocol(protocol);
            showToast('URL copiada al portapapeles', 'success');
            setTimeout(() => setCopiedProtocol(null), 2000);
        } catch (error) {
            showToast('Error al copiar al portapapeles', 'error');
        }
    };

    const initiateConnection = (config) => {
        try {
            const id = connectionData[config.idField];
            if (!id) {
                showToast(`Primero configura el ID de ${config.name}`, 'warning');
                return;
            }

            const url = config.getUrl(id);

            // Mostrar información de conexión
            showToast(`Iniciando conexión ${config.name}...`, 'info');

            // Intentar abrir la conexión
            window.open(url, '_blank');

            // Si no se abre automáticamente, mostrar instrucciones
            setTimeout(() => {
                showToast(`Si la conexión no se abrió automáticamente, copia y pega esta URL en tu ${config.clientName}: ${url}`, 'info');
            }, 1000);

        } catch (error) {
            showToast(`Error al iniciar conexión ${config.name}: ${error.message}`, 'error');
        }
    };

    const handleEditField = (field) => {
        setEditingField(field);
    };

    const handleSaveField = async (field) => {
        const value = connectionData[field];
        if (!value.trim()) {
            showToast('El campo no puede estar vacío', 'error');
            return;
        }

        try {
            const activoId = activoData.Id || activoData.id;


            if (!activoId) {
                showToast('Error: ID de activo no encontrado', 'error');
                return;
            }

            // Determinar qué API llamar según el campo
            if (field === 'rustDeskId') {
                await activosAPI.updateRustDeskId(activoId, value.trim());
            } else if (field === 'rustDeskPassword') {
                await activosAPI.updateRustDeskPassword(activoId, value.trim());
            } else if (field === 'anyDeskId') {
                await activosAPI.updateAnyDeskId(activoId, value.trim());
            } else if (field === 'anyDeskPassword') {
                await activosAPI.updateAnyDeskPassword(activoId, value.trim());
            } else if (field === 'ipAddress') {
                await activosAPI.updateIpAddress(activoId, value.trim());
            }

            setEditingField(null);
            showToast(`${field === 'ipAddress' ? 'Dirección IP guardada' : field + ' guardado'} correctamente`, 'success');

            // Notificar al componente padre para que recargue los datos
            if (onDataUpdated) {
                onDataUpdated();
            }
        } catch (error) {
            console.error('Error al guardar campo:', error);
            showToast('Error al guardar la información', 'error');
        }
    };

    const handleCancelEdit = () => {
        setEditingField(null);
    };

    const togglePasswordVisibility = (type) => {
        setShowPasswords(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    const getConnectionInfo = () => {
        return {
            hostname: activoData?.NombreEquipo || activoData?.nombreEquipo || activoData?.Codigo || activoData?.codigo || 'EQUIPO-001',
            ip: connectionData.ipAddress || 'Sin configurar',
            anydeskId: connectionData.anyDeskId,
            rustdeskId: connectionData.rustDeskId
        };
    };

    const connectionInfo = getConnectionInfo();

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Monitor className="h-5 w-5 mr-2 text-blue-600" />
                    Conexión Remota
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Conecta remotamente a este equipo usando AnyDesk o RustDesk
                </p>
            </div>

            <div className="p-6 space-y-6">
                {/* Información de Conexión */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-all hover:shadow-md">
                    <div className="p-4 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                            <Monitor className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                            Información de Red
                        </h4>
                    </div>
                    <div className="p-4 sm:p-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Hostname */}
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                    Hostname
                                </span>
                                <div className="flex items-center px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700 text-sm">
                                    <span className="font-mono text-gray-900 dark:text-white">{connectionInfo.hostname}</span>
                                </div>
                            </div>

                            {/* Dirección IP */}
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                    Dirección IP
                                    {editingField !== 'ipAddress' && (
                                        <button
                                            onClick={() => handleEditField('ipAddress')}
                                            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors flex items-center text-xs"
                                        >
                                            <Edit2 className="w-3 h-3 mr-1" /> Editar
                                        </button>
                                    )}
                                </span>
                                <div className={`flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg border ${editingField === 'ipAddress' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-100 dark:border-gray-700'}`}>
                                    {editingField === 'ipAddress' ? (
                                        <div className="flex items-center w-full">
                                            <input
                                                type="text"
                                                value={connectionData.ipAddress}
                                                onChange={(e) => setConnectionData(prev => ({ ...prev, ipAddress: e.target.value }))}
                                                className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm font-mono text-gray-900 dark:text-white"
                                                placeholder="Ej: 192.168.1.100"
                                                autoFocus
                                            />
                                            <div className="flex items-center space-x-2 ml-2">
                                                <button
                                                    onClick={() => handleSaveField('ipAddress')}
                                                    className="p-1.5 text-white bg-green-500 hover:bg-green-600 rounded-md transition-colors"
                                                    title="Guardar"
                                                >
                                                    <Save className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={handleCancelEdit}
                                                    className="p-1.5 text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                                                    title="Cancelar"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="flex-1 text-sm font-mono text-gray-900 dark:text-white py-0.5">
                                            {connectionInfo.ip}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Configuración de credenciales */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-all hover:shadow-md">
                    <div className="p-4 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                            <Monitor className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                            Credenciales de Acceso Remoto
                        </h4>
                    </div>

                    <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                        {/* AnyDesk */}
                        <div className="p-4 sm:p-5">
                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                <span className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-1.5 rounded-lg mr-2">
                                    <Monitor className="w-4 h-4" />
                                </span>
                                AnyDesk
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="flex flex-col">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                        ID de Conexión
                                        {editingField !== 'anyDeskId' && (
                                            <button onClick={() => handleEditField('anyDeskId')} className="text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors flex items-center text-xs">
                                                <Edit2 className="w-3 h-3 mr-1" /> Editar
                                            </button>
                                        )}
                                    </span>
                                    <div className={`flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg border ${editingField === 'anyDeskId' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-100 dark:border-gray-700'}`}>
                                        {editingField === 'anyDeskId' ? (
                                            <div className="flex items-center w-full">
                                                <input
                                                    type="text"
                                                    value={connectionData.anyDeskId}
                                                    onChange={(e) => setConnectionData(prev => ({ ...prev, anyDeskId: e.target.value }))}
                                                    className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white"
                                                    placeholder="Ej: 123 456 789"
                                                    autoFocus
                                                />
                                                <div className="flex items-center space-x-1 ml-2">
                                                    <button onClick={() => handleSaveField('anyDeskId')} className="p-1.5 text-white bg-green-500 hover:bg-green-600 rounded-md"><Save className="w-3.5 h-3.5" /></button>
                                                    <button onClick={handleCancelEdit} className="p-1.5 text-white bg-red-500 hover:bg-red-600 rounded-md"><X className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </div>
                                        ) : (
                                            <input type="text" value={connectionData.anyDeskId || 'No configurado'} readOnly className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white" />
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                        Contraseña
                                        {editingField !== 'anyDeskPassword' && (
                                            <div className="flex space-x-3">
                                                <button onClick={() => togglePasswordVisibility('anyDesk')} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 transition-colors">
                                                    {showPasswords.anyDesk ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                                </button>
                                                <button onClick={() => handleEditField('anyDeskPassword')} className="text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors flex items-center text-xs">
                                                    <Edit2 className="w-3 h-3 mr-1" /> Editar
                                                </button>
                                            </div>
                                        )}
                                    </span>
                                    <div className={`flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg border ${editingField === 'anyDeskPassword' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-100 dark:border-gray-700'}`}>
                                        {editingField === 'anyDeskPassword' ? (
                                            <div className="flex items-center w-full">
                                                <input
                                                    type="text"
                                                    value={connectionData.anyDeskPassword}
                                                    onChange={(e) => setConnectionData(prev => ({ ...prev, anyDeskPassword: e.target.value }))}
                                                    className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white"
                                                    placeholder="Contraseña"
                                                    autoFocus
                                                />
                                                <div className="flex items-center space-x-1 ml-2">
                                                    <button onClick={() => handleSaveField('anyDeskPassword')} className="p-1.5 text-white bg-green-500 hover:bg-green-600 rounded-md"><Save className="w-3.5 h-3.5" /></button>
                                                    <button onClick={handleCancelEdit} className="p-1.5 text-white bg-red-500 hover:bg-red-600 rounded-md"><X className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </div>
                                        ) : (
                                            <input type={showPasswords.anyDesk ? "text" : "password"} value={connectionData.anyDeskPassword || '••••••••'} readOnly className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RustDesk */}
                        <div className="p-4 sm:p-5">
                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 p-1.5 rounded-lg mr-2">
                                    <Monitor className="w-4 h-4" />
                                </span>
                                RustDesk
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="flex flex-col">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                        ID de Conexión
                                        {editingField !== 'rustDeskId' && (
                                            <button onClick={() => handleEditField('rustDeskId')} className="text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors flex items-center text-xs">
                                                <Edit2 className="w-3 h-3 mr-1" /> Editar
                                            </button>
                                        )}
                                    </span>
                                    <div className={`flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg border ${editingField === 'rustDeskId' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-100 dark:border-gray-700'}`}>
                                        {editingField === 'rustDeskId' ? (
                                            <div className="flex items-center w-full">
                                                <input
                                                    type="text"
                                                    value={connectionData.rustDeskId}
                                                    onChange={(e) => setConnectionData(prev => ({ ...prev, rustDeskId: e.target.value }))}
                                                    className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white"
                                                    placeholder="Ej: 123 456 789"
                                                    autoFocus
                                                />
                                                <div className="flex items-center space-x-1 ml-2">
                                                    <button onClick={() => handleSaveField('rustDeskId')} className="p-1.5 text-white bg-green-500 hover:bg-green-600 rounded-md"><Save className="w-3.5 h-3.5" /></button>
                                                    <button onClick={handleCancelEdit} className="p-1.5 text-white bg-red-500 hover:bg-red-600 rounded-md"><X className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </div>
                                        ) : (
                                            <input type="text" value={connectionData.rustDeskId || 'No configurado'} readOnly className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white" />
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                        Contraseña
                                        {editingField !== 'rustDeskPassword' && (
                                            <div className="flex space-x-3">
                                                <button onClick={() => togglePasswordVisibility('rustDesk')} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 transition-colors">
                                                    {showPasswords.rustDesk ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                                </button>
                                                <button onClick={() => handleEditField('rustDeskPassword')} className="text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors flex items-center text-xs">
                                                    <Edit2 className="w-3 h-3 mr-1" /> Editar
                                                </button>
                                            </div>
                                        )}
                                    </span>
                                    <div className={`flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg border ${editingField === 'rustDeskPassword' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-100 dark:border-gray-700'}`}>
                                        {editingField === 'rustDeskPassword' ? (
                                            <div className="flex items-center w-full">
                                                <input
                                                    type="text"
                                                    value={connectionData.rustDeskPassword}
                                                    onChange={(e) => setConnectionData(prev => ({ ...prev, rustDeskPassword: e.target.value }))}
                                                    className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white"
                                                    placeholder="Contraseña"
                                                    autoFocus
                                                />
                                                <div className="flex items-center space-x-1 ml-2">
                                                    <button onClick={() => handleSaveField('rustDeskPassword')} className="p-1.5 text-white bg-green-500 hover:bg-green-600 rounded-md"><Save className="w-3.5 h-3.5" /></button>
                                                    <button onClick={handleCancelEdit} className="p-1.5 text-white bg-red-500 hover:bg-red-600 rounded-md"><X className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </div>
                                        ) : (
                                            <input type={showPasswords.rustDesk ? "text" : "password"} value={connectionData.rustDeskPassword || '••••••••'} readOnly className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Opciones de conexión rápidas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {connectionConfigs.map((config) => {
                        const id = connectionData[config.idField];
                        const url = id ? config.getUrl(id) : '';
                        const displayUrl = id ? config.getDisplayUrl(id) : 'No configurado';

                        return (
                            <div key={config.protocol} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-5 flex flex-col h-full transition-all hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800">
                                <div className="flex items-start mb-4">
                                    <div className={`p-2 rounded-lg mr-3 ${config.name === 'AnyDesk' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                                        <Monitor className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h5 className="font-semibold text-gray-900 dark:text-white">Conexión a {config.name}</h5>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">{displayUrl}</p>
                                    </div>
                                </div>

                                <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700/50">
                                    {id ? (
                                        <button
                                            onClick={() => copyToClipboard(url, config.protocol)}
                                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex items-center text-xs font-medium"
                                            title="Copiar URL"
                                        >
                                            {copiedProtocol === config.protocol ? (
                                                <><CheckCircle className="h-4 w-4 mr-1 text-green-500" /> Copiado</>
                                            ) : (
                                                <><Copy className="h-4 w-4 mr-1" /> Copiar URL</>
                                            )}
                                        </button>
                                    ) : (
                                        <span className="text-xs text-amber-500 dark:text-amber-400 flex items-center">
                                            Faltan credenciales
                                        </span>
                                    )}

                                    <button
                                        onClick={() => initiateConnection(config)}
                                        disabled={!id}
                                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${id
                                            ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                            }`}
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        <span>Conectar</span>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Información adicional */}
                <div className="mt-8 p-5 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30">
                    <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-3 flex items-center text-sm">
                        <Monitor className="h-4 w-4 mr-2" />
                        Instrucciones Previas a Conectar
                    </h4>
                    <ul className="text-sm text-blue-800/80 dark:text-blue-300/80 space-y-2 ml-1">
                        <li className="flex items-start"><span className="mr-2 text-blue-400">•</span> Configura primero el ID y contraseña de cada herramienta.</li>
                        <li className="flex items-start"><span className="mr-2 text-blue-400">•</span> Asegúrate de tener instalado el cliente correspondiente en tu equipo.</li>
                        <li className="flex items-start"><span className="mr-2 text-blue-400">•</span> El equipo destino debe estar encendido y conectado a la red.</li>
                        <li className="flex items-start"><span className="mr-2 text-blue-400">•</span> Para conexiones externas, verifica que los puertos estén abiertos.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
